import React, { useMemo, useState } from 'react';
import { useAppSelector } from '@/hooks/useAppSelector';

interface PageScreenshotViewerProps {
  slug: string;
  subslug?: string | null;
}

interface VersionLabel {
  version: string;
  label: string;
  verified?: boolean;
}

const PageScreenshotViewer: React.FC<PageScreenshotViewerProps> = ({ slug, subslug }) => {
  // Get GraphQL company data from Redux
  const company = useAppSelector(state => state.graphql.data);

  // Find the relevant page
  const page = useMemo(() => {
    if (!company?.pages) return null;
    const foundPage = subslug ? company.pages.find(p => p.subslug === subslug) : company.pages.find(p => !p.subslug);
    
    // Log the page and its scrapes
    console.log('Found page:', foundPage);
    console.log('Page scrapes:', foundPage?.scrapes);
    
    return foundPage;
  }, [company, subslug]);

  // Build version options directly from scrapes
  const versions: VersionLabel[] = useMemo(() => {
    if (!page?.scrapes?.length) {
      console.log('No scrapes found!');
      return [];
    }

    console.log('Raw scrapes array:', page.scrapes);

    // Sort scrapes: verified first, then by version in descending order (newest first)
    const sortedScrapes = [...page.scrapes].sort((a, b) => {
      // First, prioritize verified scrapes
      if (a.verified && !b.verified) return -1;
      if (!a.verified && b.verified) return 1;

      // Then sort by version in descending order (newest first)
      return (b.version || '').localeCompare(a.version || '');
    });

    console.log('Sorted scrapes:', sortedScrapes);
    
    // Simply map all scrapes to version labels
    const result = sortedScrapes.map(scrape => {
      if (!scrape.version) {
        console.log('Skipping scrape without version:', scrape);
        return null;
      }
      
      return {
        version: scrape.version,
        label: scrape.quarter ? `${scrape.quarter} (${scrape.version})` : scrape.version,
        verified: scrape.verified
      };
    }).filter(Boolean) as VersionLabel[];
    
    console.log('Final versions array for dropdown:', result);
    return result;
  }, [page]);

  const [activeVersion, setActiveVersion] = useState(versions[0]?.version);

  // Find active scrape with screenshot and other URLs
  const activeScrape = useMemo(() => {
    if (!page || !activeVersion) return null;
    return page.scrapes?.find(s => s.version === activeVersion) || null;
  }, [page, activeVersion]);

  const screenshot = activeScrape?.screenshot_url || null;
  const htmlUrl = activeScrape?.html_url || null;
  const markdownUrl = activeScrape?.markdown_url || null;

  if (!page) {
    return <div className="text-sm text-gray-500">No data available.</div>;
  }

  return (
    <div className="space-y-4">
      {/* Controls bar with dropdown on left, links on right */}
      <div className="flex justify-between items-center">
        {/* Version selector on left */}
        <select
          className="border p-1 rounded"
          value={activeVersion}
          onChange={e => setActiveVersion(e.target.value)}
          disabled={versions.length <= 1}
        >
          {versions.map(v => (
            <option key={v.version} value={v.version}>
              {v.label} {v.verified ? '✓' : ''}
            </option>
          ))}
        </select>

        {/* Links to resources on right */}
        <div className="flex space-x-4 text-sm">
          {screenshot && (
            <a 
              href={screenshot} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              View Image
            </a>
          )}
          {htmlUrl && (
            <a 
              href={htmlUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              View HTML
            </a>
          )}
          {markdownUrl && (
            <a 
              href={markdownUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              View Markdown
            </a>
          )}
        </div>
      </div>

      {/* Screenshot display */}
      {screenshot ? (
        <img src={screenshot} alt={`Screenshot ${activeVersion}`} className="max-w-full" />
      ) : (
        <div className="text-sm text-gray-500">No screenshot available.</div>
      )}
    </div>
  );
};

export default PageScreenshotViewer;
